export interface AIModel {
  arn: string;
  name: string;
  description: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface InvokeModelRequest {
  modelId: string;
  payload: InvokeModelRequestNova | InvokeModelRequestSonnet4;
  s3Key?: string; // For backward compatibility
  documentId?: string; // Platform document ID (UUID from Document.DocumentID)
  expectedOutputSchema?: string;
  promptId?: number; // Optional prompt ID (for async invocations)
  promptVersion?: string; // Optional prompt version (for async invocations)
}

export function newInvokeModelRequestNova(): InvokeModelRequestNova {
  return {
    messages: [],
    system: [],
    max_tokens: 4000,
  };
}

export interface InvokeModelRequestNova {
  messages: NovaMessage[];
  system?: NovaSystemMessage[];
  max_tokens: number;
}

export interface NovaMessage {
  role: 'user' | 'assistant';
  content: NovaMessageContent[];
}

export interface NovaMessageContent {
  text: string;
}

export interface NovaSystemMessage {
  text: string;
}

// Create a new InvokeModelRequest for Sonnet4
export function newInvokeModelRequestSonnet4(): InvokeModelRequestSonnet4 {
  return {
    anthropic_version: 'bedrock-2023-05-31',
    max_tokens: 4000,
    messages: [],
    system: [],
  };
}

// Request structure for invoking the Sonnet4 model
// @anthropic_version: 'bedrock-2023-05-31' refers to the second-generation "Messages" API version for Claude models in Bedrock.
// @max_tokens: 4000 is a common starting point for token limits in AI models, allowing for substantial input and output text.
export interface InvokeModelRequestSonnet4 {
  anthropic_version: string;
  max_tokens: number; // start with 4000
  messages: Sonnet4Message[];
  system?: Sonnet4SystemMessage[];
}

export interface Sonnet4Message {
  role: 'user' | 'assistant';
  content: Sonnet4MessageContent[];
}

export interface Sonnet4MessageContent {
  text: string;
  type: 'text';
}

export interface Sonnet4SystemMessage {
  text: string;
  type: 'text';
}

export interface InvokeModelResponse {
  text: string;
  inputTokensUsed: number;
  outputTokensUsed: number;
}

export interface AIPrompt {
  promptVersionId: number;
  promptId: number | string;
  name: string;
  version: string;
  description: string;
  systemPrompt: string;
  userPrompt: string;
  temperature: string;
  maxTokens: number;
  isDraft: boolean;
  expectedOutputSchema?: string; // JSON string for expected output schema
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  deletedAt: string; // ISO 8601 date string
}

export interface PromptVariant {
  modelId?: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  stopSequences?: string[];
}

export interface CreatePromptRequest {
  name: string; // The name of the prompt
  description: string; // A description of what the prompt does
  userPrompt: string; // The actual prompt text content
  systemPrompt?: string; // Optional system prompt
  tags?: { [key: string]: string }; // Optional tags for the prompt
  defaultVariant?: PromptVariant; // The optional default variant configuration
}

export interface UpdatePromptRequest {
  name?: string;
  description?: string;
  systemPrompt?: string;
  userPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  expectedOutputSchema?: string | null;
}

export interface UpdatePromptResponse {
  id: string;
  name: string;
  version: string;
  updatedAt: string;
}

export interface DeletePromptResponse {
  id: string;
  version?: string;
  message: string;
}

export interface AIPromptResult {
  id: number;
  promptMessage: string;
  promptArn?: string;
  promptName?: string;
  modelArn: string;
  documentName?: string;
  documentArn?: string;
  documentId?: number; // Integer ID from Document.ID
  documentAccountId?: number; // AccountID for platform document downloads
  documentUuid?: string; // UUID string from Document.DocumentID for download API
  userId: number;
  userEmail: string;
  rawOutput: string;
  createdAt: string;
}

export interface CreatePromptResultRequest {
  promptId: number;
  promptVersion: string;
  promptMessage: string;
  modelArn: string;
  documentName?: string;
  documentArn?: string; // For backward compatibility
  documentId?: string; // Platform document ID (UUID from Document.DocumentID)
  rawOutput: string;
}

export interface CreatePromptResultResponse {
  message: string;
}

export interface AIPromptMapping {
  id: number;
  promptId: number;
  promptVersion: string;
  coverageTypeId: number;
  documentTypeId: number;
}

export interface AIPromptMappingRequest {
  promptId: number;
  promptVersion: string;
  coverageTypeId: number;
  documentTypeId: number;
}

export interface AIPromptMappingUpdateRequest {
  promptId?: number;
  promptVersion?: string;
  coverageTypeId?: number;
  documentTypeId?: number;
}

export interface PublishPromptVersionResponse {
  message: string;
  version: string;
}

export type ModelInvocationStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface ModelInvocation {
  invocationId: string;
  status: ModelInvocationStatus;
  modelArn: string;
  promptId?: number;
  promptVersion?: string;
  documentId?: number;
  documentName?: string;
  userId: number;
  userEmail: string;
  errorMessage?: string;
  promptResultId?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ModelInvocationStatusResponse {
  invocationId: string;
  status: ModelInvocationStatus;
  errorMessage?: string;
  completedAt?: string;
  promptResultId?: number;
}

export interface InvokeModelAsyncResponse {
  invocationId: string;
}

// Union type for ResultsPage display
export type ResultsPageItem = AIPromptResult | ModelInvocation;

// =====================================================
// Field Override Types (CAC-5113: AI Field Override Review Mode)
// =====================================================

export type OverrideFieldType = 'string' | 'number' | 'boolean';

export interface FieldOverride {
  id: number;
  promptResultId: number;
  fieldPath: string;
  fieldType: OverrideFieldType;
  originalValue: string | number | boolean | null;
  overrideValue: string | number | boolean;
  userEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface FieldOverrideListResponse {
  overrides: FieldOverride[];
  totalCount: number;
}

export interface CreateFieldOverrideRequest {
  fieldPath: string;
  overrideValue: string | number | boolean;
}

export interface MergedPromptResultResponse extends AIPromptResult {
  mergedOutput: string;
  overrideCount: number;
  overriddenFields: string[];
}

// =====================================================
// Review Types (CAC-5113: Track who reviewed extraction results)
// =====================================================

// Review summary for list display
export interface ReviewSummary {
  userEmail: string;
  reviewedAt: string; // ISO 8601 datetime
}

// Full review record
export interface AIPromptResultReview {
  id: number;
  promptResultId: number;
  userId: number;
  userEmail: string;
  reviewedAt: string;
}

// Prompt result with review info (from /prompt-results-with-reviews)
export interface AIPromptResultWithReviews extends AIPromptResult {
  reviews: ReviewSummary[];
  reviewCount: number;
}

// Response from marking as reviewed
export interface CreateReviewResponse {
  message: string;
  reviewedAt: string;
}

export interface ReviewPreviewChange {
  field: string;
  currentValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
}

export interface ReviewPreviewResponse {
  policyId: number;
  policyNumber: string;
  changes: ReviewPreviewChange[];
}
